import { Eye, Lock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { LetterTemplate } from "@/features/letters/types";
import { cn } from "@/lib/utils";

/** Recipient → pill label + token-based colours (Client=blue, IRS=amber, else neutral). */
export const RECIPIENT_META: Record<string, { label: string; pill: string }> = {
  CLIENT: { label: "Client", pill: "bg-info-soft text-info" },
  IRS: { label: "IRS", pill: "bg-warning-soft text-warning" },
  OTHER: { label: "Other", pill: "bg-muted text-muted-foreground" },
};

export function recipientMeta(recipient: string) {
  return RECIPIENT_META[recipient] ?? { label: recipient, pill: "bg-muted text-muted-foreground" };
}

/** Render a subject, turning `{merge_field}` substrings into inline code chips. */
function SubjectWithChips({ subject }: { subject: string }) {
  const parts = subject.split(/(\{[^}]+\})/g).filter(Boolean);
  return (
    <span className="text-muted-foreground text-xs leading-relaxed">
      Subject ·{" "}
      {parts.map((part, i) =>
        part.startsWith("{") && part.endsWith("}") ? (
          <code
            key={i}
            className="bg-muted text-muted-foreground mx-0.5 rounded-[6px] px-1.5 py-0.5"
          >
            {part}
          </code>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </span>
  );
}

/** Decorative letter thumbnail (skeleton bars on a white "page" with an accent top rule). */
function LetterThumbnail() {
  return (
    <div className="bg-muted flex h-[132px] flex-col overflow-hidden px-5 pt-[18px]">
      <div className="bg-card border-success flex h-full flex-col gap-1.5 rounded-t-[4px] border-t-[3px] px-4 pt-3.5 shadow-[0_-2px_12px_-6px_rgba(18,26,46,0.3)]">
        <span className="flex justify-between">
          <span className="bg-success h-2 w-11 rounded-[2px]" />
          <span className="bg-border h-1 w-14 rounded-[2px]" />
        </span>
        <span className="bg-muted-foreground/40 mt-2 h-1 w-24 rounded-[2px]" />
        <span className="bg-foreground/80 mt-0.5 h-1.5 w-36 rounded-[2px]" />
        <span className="bg-border h-1 w-full rounded-[2px]" />
        <span className="bg-border h-1 w-[92%] rounded-[2px]" />
        <span className="bg-border h-1 w-[70%] rounded-[2px]" />
      </div>
    </div>
  );
}

interface Props {
  template: LetterTemplate;
  isAdmin: boolean;
  onClone: (t: LetterTemplate) => void;
  onEdit: (t: LetterTemplate) => void;
  onDelete: (t: LetterTemplate) => void;
  onToggleHide: (t: LetterTemplate) => void;
  onPreview: (t: LetterTemplate) => void;
}

/**
 * One letter template in the gallery — decorative thumbnail with a hover overlay
 * (Preview + Clone/Edit), recipient badge, subject with merge-field chips, and a
 * role-aware footer: system templates show a read-only lock + Hide/Restore; firm
 * templates (admin) get Edit + Delete.
 */
export function LetterTemplateCard({
  template: t,
  isAdmin,
  onClone,
  onEdit,
  onDelete,
  onToggleHide,
  onPreview,
}: Props) {
  const meta = recipientMeta(t.recipient);
  return (
    <Card
      className={cn(
        "group hover:shadow-card-hover relative gap-0 overflow-hidden p-0 transition-shadow",
        t.isHidden && "opacity-60",
      )}
    >
      {/* Thumbnail + hover overlay */}
      <div className="relative">
        <LetterThumbnail />
        <div className="bg-brand-panel/55 absolute inset-0 flex items-center justify-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
          <Button type="button" size="sm" variant="secondary" onClick={() => onPreview(t)}>
            <Eye /> Preview
          </Button>
          {isAdmin &&
            (t.isSystem ? (
              <Button type="button" size="sm" onClick={() => onClone(t)}>
                Clone &amp; edit
              </Button>
            ) : (
              <Button type="button" size="sm" onClick={() => onEdit(t)}>
                Edit
              </Button>
            ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-2.5 px-5 pt-4 pb-[18px]">
        <div className="flex items-start justify-between gap-2.5">
          <span className="text-[15px] leading-snug font-semibold">{t.name}</span>
          <span
            className={cn(
              "shrink-0 rounded-full px-2 py-[3px] text-[11px] font-bold tracking-[0.06em] uppercase",
              meta.pill,
            )}
          >
            {meta.label}
          </span>
        </div>
        <SubjectWithChips subject={t.subject} />

        {/* Footer */}
        <div className="text-muted-foreground mt-0.5 flex items-center justify-between gap-2 border-t pt-2.5 text-xs">
          {t.isSystem ? (
            <span className="inline-flex items-center gap-1.5">
              <Lock className="size-3" /> System · read-only
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 font-medium">Firm template</span>
          )}
          {isAdmin &&
            (t.isSystem ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-muted-foreground h-7 px-2.5"
                onClick={() => onToggleHide(t)}
              >
                {t.isHidden ? "Restore" : "Hide"}
              </Button>
            ) : (
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground h-7 px-2.5"
                  onClick={() => onEdit(t)}
                >
                  Edit
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive h-7 px-2.5"
                    >
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete &quot;{t.name}&quot;?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This template will no longer be available when generating letters. This
                        can&apos;t be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onDelete(t)}
                        className="bg-destructive text-white hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
        </div>
      </div>
    </Card>
  );
}
